import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class NormalizeService {

  private onlyNumbers(value?: string | null): string {
    return (value ?? '').replace(/\D/g, '');
  }

  // =========================
  // INPUT / NORMALIZAÇÃO
  // =========================

  toNumber(value: string | null): number {
    if (!value) return 0;
    return Number(value.replace(/\./g, '').replace(',', '.'));
  }

  toNumberDecimal(value: string): number {
    if (!value) return 0;

    return Number(
      value
        .replace('R$', '')
        .replace(/\./g, '')
        .replace(',', '.')
        .trim()
    );
  }

  toBool(value: string | null): boolean {
    return value?.toLowerCase() === 'sim';
  }

  toDate(value: string | null): Date | null {
    if (!value) return null;

    const clean = value.trim();

    const asDate = new Date(clean);
    if (!isNaN(asDate.getTime())) {
      return asDate;
    }

    const regex =
      /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}):(\d{2}))?$/;
    const match = clean.match(regex);

    if (match) {
      const [, dd, mm, yyyy, hh = '00', min = '00', ss = '00'] = match;
      return new Date(`${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}`);
    }

    return null;
  }

  toDateLocal(value: string | null): Date | null {
    if (!value) return null;

    const clean = value.split('T')[0];
    const parts = clean.split('-');
    if (parts.length !== 3) return null;

    const [year, month, day] = parts.map(Number);
    if (!year || !month || !day) return null;

    return new Date(year, month - 1, day, 12);
  }

  toDateIso(value: string | Date | null): string {
    if (!value) return '';
    if (value instanceof Date) {
      return value.toISOString().substring(0, 10);
    }
    return value;
  }

  // =========================
  // OUTPUT / FORMATAÇÃO
  // =========================

  private readonly currencyFormatter = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2
  });

  private readonly numberFormatter = new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  currency(value?: number | null): string {
    return this.currencyFormatter.format(value ?? 0);
  }

  percent(value?: number | null, digits = 2): string {
    return `${(value ?? 0).toFixed(digits)}%`;
  }

  number(value?: number | null, digits = 2): string {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits
    }).format(value ?? 0);
  }

  date(value?: Date | string | null): string {
    if (!value) return '';

    const date =
      typeof value === 'string' ? new Date(value) : value;

    if (isNaN(date.getTime())) return '';

    return date.toLocaleDateString('pt-BR');
  }
  dateTime(value?: Date | string | null): string {
    if (!value) return '';

    const date =
      typeof value === 'string' ? new Date(value) : value;

    if (isNaN(date.getTime())) return '';

    return date.toLocaleString('pt-BR');
  }

  cpf(value?: string | null): string {
    const v = this.onlyNumbers(value);
    if (v.length !== 11) return value ?? '';

    return v.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }

  cnpj(value?: string | null): string {
    const v = this.onlyNumbers(value);
    if (v.length !== 14) return value ?? '';

    return v.replace(
      /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
      '$1.$2.$3/$4-$5'
    );
  }

  cpfCnpj(value?: string | null): string {
    const v = this.onlyNumbers(value);

    if (v.length === 11) return this.cpf(v);
    if (v.length === 14) return this.cnpj(v);

    return value ?? '';
  }

  cpfOculto(value?: string | null): string {
    const v = this.onlyNumbers(value);
    if (v.length !== 11) return value ?? '';

    return `***.${v.substring(3, 6)}.${v.substring(6, 9)}-**`;
  }

  cnpjOculto(value?: string | null): string {
    const v = this.onlyNumbers(value);
    if (v.length !== 14) return value ?? '';

    return `**.${v.substring(2, 5)}.${v.substring(5, 8)}/****-${v.substring(12, 14)}`;
  }

  cpfCnpjOculto(value?: string | null): string {
    const v = this.onlyNumbers(value);

    if (v.length === 11) return this.cpfOculto(v);
    if (v.length === 14) return this.cnpjOculto(v);

    return value ?? '';
  }

  cep(value?: string | null): string {
    if (!value) return '';

    const v = value.replace(/\D/g, '');
    if (v.length !== 8) return value;

    return v.replace(/(\d{5})(\d{3})/, '$1-$2');
  }

  cepOculto(value?: string | null): string {
    if (!value) return '';

    const v = value.replace(/\D/g, '');
    if (v.length !== 8) return value;

    return `${v.substring(0, 2)}***-***`;
  }
}
